import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';

import {
    isWidget,
    PrimaryButton,
    MessagePropType,
    AlternateAnchor,
    getLanguageFromLocale,
    DefaultIntlFormatValues,
    getAffiliateDecoratedURL,
    useSession,
} from '@ratehub/base-ui';

import { PROVINCES } from '@ratehub/documents/common/v1';
import MESSAGES from '../definitions/Messages';
import PRODUCT_TYPES from '../definitions/ProductTypes';
import CreditCardShape from '../definitions/CreditCardShape';
import getEligibilityCheckerURL from '../functions/getEligibilityCheckerURL';
import isCardEligibleForReferralOffer from '../functions/isCardEligibleForReferralOffer';


function CreditCardCTA({
    onClick,
    applyText,
    buttonSize = 'medium',

    className,

    cardId,
    giftCardOffer,
    hasReferralOffer = false,
    isMonetized = false,
    cardTypes,
    prohibitedProvinces = [],

    hasEligibilityCTA = true,

    children,

    productType,
    ...otherProps
}) {
    const { interestLocation } = useSession();
    const intl = useIntl();

    const [ isEligibleForReferralOffer, setIsEligibleForReferralOffer ] = useState(false);
    const isValidEligibilityCTA = hasEligibilityCTA
        // TODO: Remove comparison to secured card when long term fix is implemented
        && !cardTypes.includes(PRODUCT_TYPES.SECURED);
    const [ displayEligibilityCTA, setDisplayEligibilityCTA ] = useState(isValidEligibilityCTA);

    // the main CTA label may depend on a cookie in the browser, which won’t be available during exporting
    useEffect(() => {
        setIsEligibleForReferralOffer(isCardEligibleForReferralOffer(hasReferralOffer));
    }, [ hasReferralOffer ]);

    // Check whether to display the eligibility CTA based on auto-detected user location
    useEffect(() => {
        setDisplayEligibilityCTA(
            isValidEligibilityCTA
            && !prohibitedProvinces.includes(interestLocation?.province && PROVINCES.ONTARIO)
        );
    }, [ isValidEligibilityCTA, prohibitedProvinces, interestLocation?.province ]);

    const language = getLanguageFromLocale(intl.locale);
    const isAWidget = isWidget();
    const isViewOfferCTA = !isAWidget && (giftCardOffer || isEligibleForReferralOffer);

    const primaryButtonMessage = isViewOfferCTA     // the primary CTA may have different labels
        ? LOCAL_MESSAGES.viewOffer                  // gift offer or referral bonus
        : isMonetized
            ? applyText || MESSAGES.ctaButtonText   // monetized
            : LOCAL_MESSAGES.learnMore;             // non-monetized

    /*
        Atypical Event Clicking
        - when there are children (as for featured products), they render above the CTA(s)
        - in that case, a click anywhere on <Container> is seen as a CTA click on the "go to site" button, handled
          by onClick (which accounts for handling gift offers and referral bonuses)
        - however, when there are two CTAs, the additional one is an anchor and wants to avoid the "go to site"
          logic, so it includes an event.stopPropagation()
    */

    const ButtonApplyNow = (
        <PrimaryButton
            className="cc-cta"
            message={primaryButtonMessage}
            onClick={children
                ? () => {}
                : onClick
            }
            size={buttonSize}
            data-name="credit-cards.apply-now"
            key="theButton"
        />
    );

    const AnchorEligibilityChecker = (
        <AlternateAnchor
            className="cc-cta"
            message={(
                <FormattedMessage
                    id="edb.cc.check-eligibility-cta"
                    defaultMessage="check eligibility"
                    values={DefaultIntlFormatValues}
                />
            )}
            onClick={event => event.stopPropagation()}
            href={getAffiliateDecoratedURL(getEligibilityCheckerURL(language, cardId, productType))}
            target={isAWidget
                ? '_blank'
                : ''
            }
            size={buttonSize}
            data-name="credit-cards.check-eligibility"
            key="theAnchor"
        />
    );


    return (
        <>
            <Container
                className={className}
                onClick={children
                    ? onClick
                    : undefined
                }
                {...otherProps}
            >
                {children}

                <div className="cta-wrapper rh-display-flex rh-flex-wrap-reverse rh-justify-content-center rh-gap-0_5">
                    <Choose>
                        <When condition={displayEligibilityCTA}>
                            {AnchorEligibilityChecker}
                            {ButtonApplyNow}
                        </When>

                        <Otherwise>
                            {ButtonApplyNow}
                        </Otherwise>
                    </Choose>
                </div>
            </Container>
        </>
    );
}


CreditCardCTA.propTypes = {
    onClick: PropTypes.func,
    applyText: MessagePropType,
    buttonSize: PropTypes.oneOf([ 'small', 'medium', 'large' ]),

    className: PropTypes.string,

    cardId: CreditCardShape.id,
    hasReferralOffer: PropTypes.bool,
    giftCardOffer: CreditCardShape.giftCardOffer,
    isMonetized: PropTypes.bool,
    cardTypes: CreditCardShape.cardTypes,

    children: PropTypes.node,
    prohibitedProvinces: CreditCardShape.prohibitedProvinces,

    hasEligibilityCTA: PropTypes.bool,
    productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)),
};


const LOCAL_MESSAGES = defineMessages({
    viewOffer: {
        id: 'edb.cc.cta.incentive.view-offer',
        defaultMessage: 'View offer',
    },

    learnMore: {
        id: 'edb.cc.cta.learn-more',
        defaultMessage: 'Learn more',
    },
});


const Container = styled.div`
    > .cta-wrapper {
        align-self: end;

        > .cc-cta {
            // we do _not_ want our text to wrap within the button or link
            white-space: nowrap;
            max-width: 18rem;
        }
    }
`;


export default observer(CreditCardCTA);
